import React, {useCallback, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {RangeCommand, RangeFilter, RangeListColumn, RangeSort, RangeTab} from "../../components/rangelist/RangeListTypes";
import {EnumShippingLabelDeliveryStatus, EnumTradeInPayoutType, FilterFindManyTradeInInput, ListsTradeInFragment, SortFindManyTradeInInput, useListsTradeInsListLazyQuery} from "../../Queries";
import {Thumbnails} from "../../components/Thumbnails";
import {BodyText} from "../../layout/Typography";
import {RangeList} from "../../components/rangelist/RangeList";
import {DateFormat} from "../../utility/DateFormat";
import {BrandTradeInPagePath} from "../brand/tradeins/BrandTradeInPage";
import {FileDownload} from "@mui/icons-material";
import {TradeInExport} from "./TradeInExport";
import {Alert, Box, Chip} from "@mui/material";
import {TradeInItemExport} from "./TradeInItemExport";
import {generateMonthOptions} from "../../utility/FilterHelper";

type ListsTradeInsListPageProps = {
   globalId: string
   extrafilter?: Partial<FilterFindManyTradeInInput>
   extracolumns?: RangeListColumn<ListsTradeInFragment>[]
   extracolumnsprepend?: boolean
   extraCommands?: RangeCommand<ListsTradeInFragment>[]
   viewId?: string
}

export const ListTradeInsList = ({
                                    globalId,
                                    extrafilter,
                                    extracolumns,
                                    extracolumnsprepend,
                                    extraCommands,
                                    viewId
                                 }: ListsTradeInsListPageProps) => {
   const [fetchMore] = useListsTradeInsListLazyQuery();
   const [tradeIns, setTradeIns] = React.useState<string[]>([])
   const [exportIds, setExportIds] = React.useState<string[]>([])

   const navigate = useNavigate();

   const commands: RangeCommand<ListsTradeInFragment>[] = [{
      id: 'exportTradeIns',
      title: 'Export TradeIns to excel',
      icon: FileDownload,
      execute: async (rowIds) => {
         setTradeIns(rowIds)
      }
   }, {
      id: 'exportItems',
      title: 'Export Items to excel',
      icon: FileDownload,
      execute: async (rowIds) => {
         setExportIds(rowIds)
      }
   }]

   let columns: RangeListColumn<ListsTradeInFragment>[] = [{
      id: 'count',
      label: '#',
      get: (row) => {
         return row.tradeInNr
      },
      width: 4
   }, {
      id: 'items',
      label: 'Items',
      field: '_id',
      render: (data, row) => {
         const imageUrls: string[] = [];
         const hasBonus = row?.items?.some(item => item?.bonus);

         row?.items?.forEach(item => {
            if (item?.imageUrls?.length) {
               imageUrls.push(...item?.imageUrls)
            }
         })

         return (
            <Box sx={{position: 'relative'}}>
               <Thumbnails legend={String(row?.items?.length || 0)} maxImages={3} imageUrls={imageUrls} size={60}/>
               {hasBonus && <Chip size="small" color="primary" label="Bonus" sx={{padding: '2px 4px', transform: 'translate(5px, 10px) rotate(45deg);', marginTop: '4px', position: 'absolute', top: 0, left: 0}}/>}
            </Box>
         )
      },
      width: 11
   }, {
      id: 'name',
      label: 'Navn, Mail',
      get: (row) => {
         return row.consumer?.name + ' ' + row.consumer?.mail
      },
      render: (data, row) => {
         return <div style={{overflow: 'hidden', maxWidth: '180px', whiteSpace: 'nowrap'}}>
            <BodyText type={"body2"} title={row?.consumer?.name || ''}>{row?.consumer?.name}</BodyText>
            <BodyText type={"subtitle2"} title={row?.consumer?.mail || ''}>{row?.consumer?.mail}</BodyText>
         </div>
      },
      width: 23
   }, {
      id: 'country',
      label: 'Country',
      get: (row) => {
         return row.consumer?.country || ''
      },
      width: 8
   }, {
      id: 'shopId',
      label: 'Store',
      get: (row) => {
         return <Box>
            <BodyText type={"body2"}>{row.shop?.name}</BodyText>
            <BodyText type={"subtitle1"}> {row.source || 'ONLINE'}</BodyText>
         </Box>
      },
      width: 12
   }, {
      id: 'date',
      label: 'Date',
      field: 'dateTime',
      render: (date, row) => {
         return DateFormat.toPresent(date)
      },
      width: 10
   }, {
      id: 'reward',
      label: 'Reward',
      get: (row) => {
         return row._id
      },
      render: (id, row) => {
         const first = row?.vouchers?.[0]
         if (first) {
            if (row?.payoutType === EnumTradeInPayoutType.PAYOUT) {
               const totalPayout = row?.vouchers?.reduce((a, b) => a + (b?.payoutValue || 0), 0);
               return <div key={'tradein_voucher_' + first?.code}>
                  <div style={{marginBottom: '1px'}}>
                     Payout: {totalPayout + ' ' + first?.currency}
                  </div>
               </div>
            } else {
               const total = row?.vouchers?.reduce((a, b) => a + (b?.value || 0), 0);
               return <div key={'tradein_voucher_' + first?.code}>
                  <div style={{marginBottom: '1px'}}>
                     {total + ' ' + first?.currency}
                  </div>
               </div>
            }
         } else {
            return <div>None</div>
         }
      },
      width: 10
   }, {
      id: 'registered',
      label: 'Status',
      field: 'registered',
      get: (row) => {
         return !!row.registered
      },
      render: (registered, row) => {
         if (!registered) {
            const deliveryStatus = row?.label?.deliveryStatus;
            if (deliveryStatus === EnumShippingLabelDeliveryStatus.DELIVERED) {
               return <Chip size={"small"} color={"primary"} label={"Delivered"} sx={{opacity: 0.7}}/>
            } else if (deliveryStatus === EnumShippingLabelDeliveryStatus.PROGRESSING) {
               return <Chip size={"small"} color={"warning"} label={"In transit"}/>
            } else if (deliveryStatus === EnumShippingLabelDeliveryStatus.WAITING) {
               return <Chip size={"small"} color={"secondary"} label={"Not sent"} sx={{opacity: 0.7}}/>
            } else if (deliveryStatus === EnumShippingLabelDeliveryStatus.UNKNOWN) {
               return <Chip size={"small"} color={"secondary"} label={"No info"} sx={{opacity: 0.3}}/>
            } else if (deliveryStatus === EnumShippingLabelDeliveryStatus.WARNING) {
               return <Chip size={"small"} color={"secondary"} label={"No info"} sx={{opacity: 0.5}}/>
            } else {
               return <div>None</div>
            }
         }
         return <Chip size={"small"} color={"success"} label={"Received"}/>
      },
      width: 15
   }, {
      id: 'logistics',
      label: 'Logistics',
      get: (row) => {
         let stocked = row.items.filter(item => item.stockItem || item.brandItem).length
         let unstocked = row.items.filter(item => !item.rejected && !(item.stockItem || item.brandItem)).length
         let rejected = row.items.filter(item => item.rejected).length
         return {
            stocked: stocked,
            unstocked: unstocked,
            rejected
         }
      },
      render: (data, row) => {
         if (!row?.registered) {
            return <>Awaiting registration</>
         }
         if (data.unstocked > 0) {
            return <Alert sx={{padding: '0 5px 0 5px'}} severity={"warning"}>
               {data.stocked > 0 && <>{data.stocked} stocked<br/></>}
               {data.unstocked} in process
            </Alert>
         } else if (!!data.stocked) {
            return <Alert sx={{padding: '0 5px 0 5px'}} severity={"success"}>
               {data.stocked} stocked
            </Alert>
         } else if (!!data.rejected) {
            return <Alert sx={{padding: '0 5px 0 5px'}} severity={"error"}>{data.rejected} rejected</Alert>
         }
      },
      width: 15
   }]

   const filters: RangeFilter<FilterFindManyTradeInInput>[] = [{
      id: 'typen',
      label: 'Type',
      options: [{
         label: 'Registered',
         filter: {
            registered: true,
         }
      }]
   }, {
      id: 'payoutType',
      label: 'Payout Type',
      options: [{
         label: 'Voucher',
         filter: {
            payoutType: EnumTradeInPayoutType.VOUCHER
         }
      }, {
         label: 'Payout',
         filter: {
            payoutType: EnumTradeInPayoutType.PAYOUT
         }
      }]
   }, {
      id: 'month',
      label: 'Month',
      options: generateMonthOptions(1, 'dateTime') // Generates options for the last 2 years
   }]

   const tabsSpec: RangeTab[] = useMemo(() => {
      if (viewId) {
         return [];
      }
      return [{
         id: 'All',
         label: 'All',
         filters: []
      }, {
         id: 'Registered',
         label: 'Registered',
         filters: [{
            filterId: 'typen',
            values: ['Registered']
         }]
      }];
   }, [viewId]);

   const handleSelect = (row: ListsTradeInFragment, newWindow?: boolean) => {
      if (newWindow) {
         window.open(BrandTradeInPagePath(row.brandHouseId, row._id), '_blank');
      } else {
         navigate(BrandTradeInPagePath(row.brandHouseId, row._id));
      }
   }

   const onRequestData = useCallback(async (search: string | undefined, sort: string | undefined, page: number, perPage: number, filter?: any) => {
      const {data} = await fetchMore({
         variables: {
            page: page,
            sort: (sort || SortFindManyTradeInInput._ID_DESC) as SortFindManyTradeInInput,
            perPage: perPage,
            search: search,
            filter: {
               ...filter,
               ...extrafilter
            } as FilterFindManyTradeInInput,
            viewId: viewId
         }
      });
      return {
         data: data?.TradeInPagination?.items || [],
         totalCount: data?.TradeInPagination?.count || 0
      }
   }, [fetchMore, viewId, extrafilter]);

   const onSelectAll = useCallback(async (filter?: any): Promise<string[]> => {
      const {data} = await fetchMore({
         variables: {
            page: 1,
            sort: SortFindManyTradeInInput._ID_DESC,
            perPage: 10000,
            search: undefined,
            filter: {
               ...filter,
               ...extrafilter
            } as FilterFindManyTradeInInput,
            viewId: viewId
         }
      });
      return data?.TradeInPagination?.items?.map(item => item._id) || [];
   }, [fetchMore, viewId, extrafilter]);

   return <>
      <RangeList globalId={globalId}
                columns={columns}
                filters={filters}
                tabs={tabsSpec}
                itemsPerPage={20}
                options={{
                   itemHeight: 80
                }}
                sorts={[{
                   id: "creation",
                   label: "Sort by creation",
                   value: SortFindManyTradeInInput._ID_DESC
                }, {
                   id: "registration",
                   label: "Sort by registration",
                   value: SortFindManyTradeInInput.REGISTERED_DESC
                }]}
                commands={[...commands, ...(extraCommands || [])]}
                selections={true}
                onSelectRow={handleSelect}
                onRequestData={onRequestData}
                onSelectAll={onSelectAll}
      />
      {exportIds.length > 0 && <TradeInItemExport language={"da"} currency={"DKK"} ids={exportIds}/>}
      {tradeIns.length > 0 && <TradeInExport language={"da"} currency={"DKK"} ids={tradeIns}/>}
   </>
}
